/**
 * API communication layer for the feedback widget.
 */

interface SubmitPayload {
  title: string;
  description?: string;
  reporterName?: string;
  reporterEmail?: string;
  pageUrl?: string;
  viewportWidth?: number;
  viewportHeight?: number;
  devicePixelRatio?: number;
  userAgent?: string;
  browserMetadata?: Record<string, unknown>;
  consoleErrors?: string[];
  assigneeId?: string;
  networkFailures?: string[];
  attachments?: {
    type: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
  }[];
}

interface SubmitResult {
  id: string;
  uploadUrls: {
    type: string;
    attachmentId: string;
    uploadUrl: string;
    storageKey: string;
  }[];
}

export async function submitFeedback(
  apiUrl: string,
  widgetKey: string,
  payload: SubmitPayload
): Promise<SubmitResult> {
  const response = await fetch(`${apiUrl}/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ widgetKey, ...payload }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to submit feedback");
  }

  return response.json();
}

/**
 * Server-side proxy upload — POSTs the blob to /api/widget/upload-blob,
 * which forwards it to R2 from the server. This sidesteps the browser CORS
 * preflight that the previous direct-to-R2 PUT pattern required, so the
 * widget works on any customer domain without needing per-origin R2 CORS
 * configuration.
 */
export async function uploadAttachment(
  apiUrl: string,
  widgetKey: string,
  attachmentId: string,
  blob: Blob,
  fileName = "screenshot.png"
): Promise<void> {
  const formData = new FormData();
  formData.append("widgetKey", widgetKey);
  formData.append("attachmentId", attachmentId);
  formData.append("file", blob, fileName);

  const response = await fetch(`${apiUrl}/upload-blob`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Upload failed (${response.status})`);
  }
}

export async function checkStatus(
  apiUrl: string,
  feedbackId: string
): Promise<{ status: string }> {
  const response = await fetch(`${apiUrl}/status/${feedbackId}`);
  return response.json();
}
